export type CreateOrderRequest = {
  data: CartsOrderRequestBody
}

import { Frequency } from './RecurringDonationPlan'

// Details about the payment based on the method
export type MethodDetails =
  | AchOnlineDto
  | CreditCardOnlineDto
  | StripeOnlineDto
  | PaypalCommerceOnlineDto
  | PlaidOnlineDto
  | VenmoOnlineDto
  | PayPalOnlineDto

type AchOnlineDto = {
  accountNumber: string
  routingNumber: string
}

type StripeOnlineDto = {
  source?: string
  defaultSource?: string
  accountId?: string
  isPaymentIntent?: boolean
}

type PaypalCommerceOnlineDto =
  | {
      paypalOrderId: string
    }
  | {
      agreementId: string
    }

type PayPalOnlineDto = {
  nonce: string
}

type PlaidOnlineDto = {
  publicToken: string
  accountId: string
}

type VenmoOnlineDto = {
  venmoOrderId: string
}

type CreditCardOnlineDto = {
  token: string
  cvv?: string
  expirationMonth: number
  expirationYear: number
}

export enum OrdersPaymentSource {
  APPLEPAY = 'applePay',
  GPAY = 'googlePay',
  MANUAL = 'manual',
  PAYPAL = 'paypal',
  VENMO = 'venmo',
  PLAID = 'plaid',
  COINBASE = 'coinbase',
  WALLET = 'wallet',
}

export enum OrdersProductType {
  DONATION = 'donation',
  RECURRING = 'recurring',
}

type CartsOrderRequestBody = {
  type: 'orders'
  attributes: {
    cartId: string
  }
  reCaptchaToken?: string | null
}

export type Order = {
  id: string
  type: string
  attributes: {
    organizationId: number
    confirmationId: string
    memberId: number
    supporterId: number
    currency: string
    cartId?: string
    donorCoversFees: boolean
    orderProperties: {
      campaignId: number
      isAmountHidden: boolean
      isAnonymous: boolean
      isGiftAid: boolean
      sourceCode1: string | null
      sourceCode2: string | null
      sourceCodeReferrer: string | null
      companyName: string | null
      id: string
      createdAt: string
      updatedAt: string
      organizationId: number
    }
    paymentTokenId?: string
    orderItems: OrderItem[]
    dedication?: {
      type: string
      recipientFirstName: string
      recipientLastName: string
      recipientEmail: string
      recipientAddress1: string
      recipientAddress2: string
      recipientCity: string
      recipientState: string
      recipientCountry: string
      recipientPostalCode: string
      ecardId: number
      ecardMessage: string
      honoreeFirstName: string
      honoreeLastName: string
    }
    comment?: string
    id?: string
    status: string
    purchasedAt: string
    donorCoveredFee: number
    grandTotal: number
    subtotal: number
    createdAt: string
    updatedAt: string
    errorCode: string | null
    orderAnswers: [
      {
        id: string
        orderId: string
        organizationId: number
        questionId: number
        createdAt: string
        updatedAt: string
        value: string
      },
    ]
    grossAdjustments: {
      organizationId: number
      createdAt: string
      id: string
      type: string
      appliedApplicationFeePercent: number
      appliedFlexRatePercent: number
      appliedProcessorFeePercent: number
      appliedRawProcessorFeeFlat: number
      applicationFeeAmount: number
      flexRateAmount: number
      processorFeeAmount: number
      appliedFotPercent: number
    } | null
    paymentIntent: {
      clientSecret: string
    }
  }
  links: unknown
  relationships?: {
    property1: [
      {
        data: {
          id: string
          type: string
          meta: unknown
        }
        links: unknown
        meta: unknown
      },
    ]
    property2: [
      {
        data: {
          id: string
          type: string
          meta: unknown
        }
        links: unknown
        meta: unknown
      },
    ]
  }
  meta?: unknown
}

export type OrderItem = {
  id: string
  organizationId: number
  createdAt: string
  updatedAt: string
  productType: 'donation' | 'recurring' | 'subsequentRecurring'
  quantity: number | null
  unitPrice: number
  orderItemProperties: {
    designationId: number
    organizationId: number
    fundraisingPageId?: number | null
    fundraisingTeamId?: number | null
    id: string
    createdAt: string
    updatedAt: string
  }
  orderItemRecurringProperties?: {
    organizationId: number

    frequency: Omit<Frequency, 'one-time'>
    timeZone: string
    startDate: string
    endDate: string | null
    id: string
    createdAt: string
    updatedAt: string
  } | null
  orderItemSubsequentRecurringProperties?: {
    planId: string
    frequency: string
    id: string
    createdAt: string
    updatedAt: string
  } | null
}

export type OrderResponse = {
  data: Order
  errors?: OrdersError
}

type OrdersError = {
  id: string
  status: string
  code: string
  title: string
  detail: string
  source: object
  links: object
  meta: object
}
